import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import APIClient from '../../utils/axios.util';

const CredentialsPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hovering, setHovering] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid credentials link.');
      setLoading(false);
      return;
    }
    APIClient.get('/auth/credentials', { params: { token } })
      .then(({ data }) => {
        setPassword(data?.data?.password ?? '');
        setEmail(data?.data?.email ?? '');
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Unable to load credentials.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleCopy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed. Please select the password and copy manually.');
    }
  };

  const display = hovering && password ? password : '********';

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Your account credentials
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && password && (
          <Box sx={{ mt: 2 }}>
            {email && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Email: <strong>{email}</strong>
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Password
            </Typography>
            <Typography
              component="span"
              onMouseEnter={() => setHovering(true)}
              onMouseLeave={() => setHovering(false)}
              onClick={handleCopy}
              sx={{
                fontFamily: 'monospace',
                fontSize: '1.25rem',
                fontWeight: 700,
                letterSpacing: hovering ? 0 : 2,
                cursor: 'pointer',
                color: copied ? 'success.main' : 'text.primary',
                userSelect: hovering ? 'text' : 'none',
              }}
            >
              {display}
            </Typography>
            {copied && (
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                Copied to clipboard
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
              <Button variant="contained" component={RouterLink} to="/login">
                Log in
              </Button>
              <Button variant="outlined" component={RouterLink} to="/dashboard/demos">
                My demos
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default CredentialsPage;
